/**
 * Bindings for the backend `/v1/users/*` surface. Wire shape matches
 * `opensoi-backend/internal/users` (verified by reading the existing
 * mobile client at `opensoi-mobile/services/api/users.ts`).
 *
 * `POST /v1/users/me` is upsert-on-first-call. Subsequent calls return
 * the same row. `GET /v1/users/me` returns the user + optional stats
 * block (BE-303 — `*_count` fields default to 0 if missing).
 */

import { apiFetch } from './client';

export interface UserStats {
  reportsCount: number;
  reviewsCount: number;
  stallsAddedCount: number;
}

export interface User {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  points: number;
}

export interface MeResponse {
  user: User;
  stats: UserStats;
}

export interface UpsertMePayload {
  displayName: string;
  languagePref: 'th' | 'en';
}

interface RawUser {
  id: string;
  display_name: string;
  avatar_url: string | null;
  points: number;
  reports_count?: number;
  reviews_count?: number;
  stalls_added_count?: number;
}

function mapUser(raw: RawUser): User {
  return {
    id: raw.id,
    displayName: raw.display_name,
    avatarUrl: raw.avatar_url,
    points: raw.points,
  };
}

function mapStats(raw: RawUser): UserStats {
  return {
    reportsCount: raw.reports_count ?? 0,
    reviewsCount: raw.reviews_count ?? 0,
    stallsAddedCount: raw.stalls_added_count ?? 0,
  };
}

export const usersApi = {
  getMe: async (token: string, signal?: AbortSignal): Promise<MeResponse> => {
    const raw = await apiFetch<RawUser>('/v1/users/me', { token, signal });
    return { user: mapUser(raw), stats: mapStats(raw) };
  },
  upsertMe: async (payload: UpsertMePayload, token: string): Promise<User> => {
    const raw = await apiFetch<RawUser>('/v1/users/me', {
      method: 'POST',
      body: {
        display_name: payload.displayName,
        language_pref: payload.languagePref,
      },
      token,
    });
    return mapUser(raw);
  },
};

export const __test__ = { mapUser, mapStats };
